/**
 * AnimationControls - Playback control bar for animation.
 * Includes play/pause, speed control, scrubber, and options.
 */

import { useCallback } from 'react'
import { Pause, Play, Repeat, RotateCcw } from 'lucide-react'
import { Button } from '../ui/Button'
import { Slider } from '../ui/Slider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { useAnimation } from '../../contexts/AnimationContext'
import type { PlaybackSpeed } from '@/types/Animation.types.ts'
import { ANIMATION_DEFAULTS, SPEED_LABELS } from '@/types/Animation.types.ts'
import { useAnimationProgress, useFormattedDuration, useFormattedTime } from '@/hooks/UseAnimationEngine'
import './animation-controls.css'

type AnimationControlsProps = {
	className?: string
}

export function AnimationControls({ className }: AnimationControlsProps) {
	const {
		state,
		play,
		pause,
		reset,
		seek,
		setSpeed,
		toggleLoop,
	} = useAnimation()

	const progress = useAnimationProgress(state)
	const currentTime = useFormattedTime(state)
	const totalDuration = useFormattedDuration(state)

	const handlePlayPause = useCallback(() => {
		if (state.isPlaying) {
			pause()
		} else {
			play()
		}
	}, [state.isPlaying, play, pause])

	const handleScrubberChange = useCallback(
		(value: number[]) => {
			const newProgress = (value[0] ?? 0) / 100
			seek(newProgress)
		},
		[seek],
	)

	const handleSpeedChange = useCallback(
		(value: string) => {
			const speed = parseFloat(value) as PlaybackSpeed
			setSpeed(speed)
		},
		[setSpeed],
	)

	return (
		<div className={`animation-controls ${className || ''}`}>
			{/* Progress scrubber */}
			<div className="animation-controls-scrubber">
				<span className="animation-controls-time">
					{currentTime}
				</span>
				<Slider
					value={[progress]}
					onValueChange={handleScrubberChange}
					max={100}
					step={0.1}
					className="flex-1"
					aria-label="Animation progress"
				/>
				<span className="animation-controls-time">
					{totalDuration}
				</span>
			</div>

			{/* Control buttons */}
			<div className="animation-controls-buttons">
				<Button
					variant="ghost"
					size="icon"
					onClick={reset}
					aria-label="Reset animation"
					className="animation-controls-button-reset"
				>
					<RotateCcw className="size-5" />
				</Button>

				<Button
					variant="action"
					onClick={handlePlayPause}
					aria-label={state.isPlaying ? 'Pause animation' : 'Play animation'}
					className="animation-controls-button-play"
				>
					{state.isPlaying ? (
						<Pause className="size-6" />
					) : (
						<Play className="ml-0.5 size-6" />
					)}
				</Button>

				<Button
					variant="ghost"
					size="icon"
					onClick={toggleLoop}
					aria-label={`Toggle loop: ${state.loopMode ? 'on' : 'off'}`}
					className="animation-controls-button-loop"
					data-active={state.loopMode}
				>
					<Repeat className="size-5" />
				</Button>

				{/* Visual divider */}
				<div className="animation-controls-divider" />

				{/* Speed control inline */}
				<div className="animation-controls-speed-group">
					<span className="animation-controls-speed-label">Speed:</span>
					<Select
						value={String(state.playbackSpeed)}
						onValueChange={handleSpeedChange}
					>
						<SelectTrigger
							className="animation-controls-speed-select"
							aria-label="Playback speed"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ANIMATION_DEFAULTS.SPEED_OPTIONS.map((speed) => (
								<SelectItem key={speed} value={String(speed)}>
									{SPEED_LABELS[speed]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		</div>
	)
}
